// Page metadata (title, description, canonical, robots, JSON-LD) for every route.
//
// Pure functions without browser/React imports: SeoManager uses them at runtime and scripts/prerender.js
// compiles this same file for Node at build time (scripts/lib/load-seo.js), so both produce the same metadata.
// API_BASE is passed in as `apiBase` so the same code works in the app and in the build scripts.

const SITE_URL = 'https://pmp-online.net';
const SITE_NAME = 'PMPonline';
// Indexable pages also opt in to full-size snippets and large image previews (Search, Discover, image results).
const ROBOTS_INDEX = 'index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1';
const DEFAULT_IMAGE = `${SITE_URL}/pmp-logo-2025.png`;
const DEFAULT_DESCRIPTION = 'PMPonline ist die Vermittlungsplattform für Fortbildungen, Seminare, Referenten, Hersteller, Produkte und Versorgungspartner im Gesundheits- und Wundmanagement.';

const STATIC_ROUTES = {
  '/': {
    title: 'PMPonline | Fortbildungen, Seminare und Referenten im Wundmanagement',
    description: 'Fortbildungen, Seminare und Lehrgänge im Wundmanagement finden: aktuelle Termine, Wundexperte ICW, Referenten, Hersteller und Versorgungspartner.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/veranstaltungen': {
    title: 'Fortbildungen & Seminare im Wundmanagement | PMPonline',
    description: 'Fortbildungen, Seminare und Lehrgänge aus Wundmanagement, Pflege und Gesundheitswesen: Termine, Orte und Anmeldung für Wundexperte ICW und mehr auf PMPonline.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/referenten': {
    title: 'Referenten für Wundmanagement & Pflege | PMPonline',
    description: 'Qualifizierte Referenten für medizinische Fortbildungen, Seminare und Fachveranstaltungen im Wundmanagement und in der Pflege finden Sie auf PMPonline.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/hersteller': {
    title: 'Hersteller & Produkte im Wundmanagement | PMPonline',
    description: 'Hersteller, Produkte und medizinische Versorgungslösungen für Wundmanagement, Wundversorgung und Gesundheitsversorgung auf PMPonline.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/versorgungspartner': {
    title: 'Versorgungspartner für Wundversorgung & Pflege | PMPonline',
    description: 'Versorgungspartner und spezialisierte Anbieter für medizinische Versorgung, Pflege und Wundmanagement finden Sie auf PMPonline.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/info': {
    title: 'Über PMPonline | Plattform für Wundmanagement',
    description: 'Erfahren Sie mehr über PMPonline und die Plattform zur Vernetzung von Veranstaltern, Referenten, Herstellern und Versorgungspartnern im Gesundheitswesen.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/abo': {
    title: 'Abonnements | PMPonline',
    description: 'Abonnements und Leistungen von PMPonline für Anbieter und Organisationen im Gesundheitsmarkt: Veranstaltungen, Produkte und Profile sichtbar machen.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/app': {
    title: 'PMPonline App | PMPonline',
    description: 'Die PMPonline App: Fortbildungen, Referenten, Produkte und Versorgungspartner im Wundmanagement mobil abrufen.',
    type: 'website',
    robots: ROBOTS_INDEX,
  },
  '/datenschutz': {
    title: 'Datenschutz | PMPonline',
    description: 'Datenschutzhinweise und Informationen zur Verarbeitung personenbezogener Daten auf PMPonline.',
    type: 'article',
    robots: ROBOTS_INDEX,
  },
  '/impressum': {
    title: 'Impressum | PMPonline',
    description: 'Impressum und Anbieterkennzeichnung von PMPonline.',
    type: 'article',
    robots: ROBOTS_INDEX,
  },
  '/signin': {
    title: 'Anmelden | PMPonline',
    description: 'Melden Sie sich bei PMPonline an.',
    type: 'website',
    robots: 'noindex,nofollow',
  },
  '/signup': {
    title: 'Registrieren | PMPonline',
    description: 'Erstellen Sie ein Nutzerkonto bei PMPonline.',
    type: 'website',
    robots: 'noindex,nofollow',
  },
  '/password-reset': {
    title: 'Passwort zurücksetzen | PMPonline',
    description: 'Setzen Sie Ihr PMPonline Passwort zurück.',
    type: 'website',
    robots: 'noindex,nofollow',
  },
  '/password-new': {
    title: 'Neues Passwort | PMPonline',
    description: 'Vergeben Sie ein neues Passwort für Ihr PMPonline Konto.',
    type: 'website',
    robots: 'noindex,nofollow',
  },
  '/p-success': {
    title: 'Zahlung erfolgreich | PMPonline',
    description: 'Ihre Zahlung wurde erfolgreich verarbeitet.',
    type: 'website',
    robots: 'noindex,nofollow',
  },
};

const ADMIN_PREFIXES = ['/admin'];

// Google shows roughly 60-70 characters of a title and 150-160 of a description; anything longer is cut off
// or rewritten, so every generated value is fitted to these budgets instead of being cut mid-word.
const TITLE_MAX = 70;
const DESCRIPTION_MAX = 158;

const ORGANIZATION_ID = `${SITE_URL}#organization`;
const WEBSITE_ID = `${SITE_URL}#website`;

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
};

const formatTime = (value) => new Date(value).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

const isValidDate = (value) => Boolean(value) && !Number.isNaN(new Date(value).getTime());

// The data contains stray whitespace (e.g. "Bad Kissingen "), which would show up as double spaces in titles.
const clean = (value) => String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim();

const stripHtml = (value) => (value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const flatten = (value) => (Array.isArray(value) ? value.reduce((all, item) => all.concat(flatten(item)), []) : [value]);

// Strapi returns strings, numbers or (nested) arrays of strings; join the printable parts.
const listText = (value, max = Infinity) =>
  flatten(value)
    .filter((item) => item !== null && item !== undefined && item !== '' && typeof item !== 'object')
    .map((item) => String(item).replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .slice(0, max)
    .join(', ');

// Cut at a word boundary and mark the cut, so snippets never end mid-word.
const clip = (value, max) => {
  const text = (value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) {
    return text;
  }
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:–-]+$/, '')}…`;
};

// Title = main text + optional tails (most informative first) + brand. Facts beat the brand suffix: for each
// tail the title is tried with and then without the brand. The main text is only shortened as a last resort.
const fitTitle = (rawMain, tails = ['']) => {
  const main = clean(rawMain).replace(/[\s.:;,–-]+$/, '');
  const brand = ` | ${SITE_NAME}`;

  for (const tail of tails) {
    for (const candidate of [`${main}${tail}${brand}`, `${main}${tail}`]) {
      if (candidate.length <= TITLE_MAX) {
        return clean(candidate);
      }
    }
  }

  const room = (tail) => TITLE_MAX - tail.length;
  let chosen = tails.find((tail) => room(tail) >= 40);
  if (chosen === undefined) {
    chosen = tails.find((tail) => room(tail) >= 24);
  }
  return chosen === undefined ? clip(main, TITLE_MAX) : clean(`${clip(main, room(chosen))}${chosen}`);
};

// Description = key facts, then a snippet of the page text in the remaining space, then a call to action if it fits.
const fitDescription = (facts, body, callToAction) => {
  const head = facts.map(clean).filter(Boolean).join(' · ');
  let text = clip(head, DESCRIPTION_MAX);
  if (body) {
    const room = DESCRIPTION_MAX - head.length - 3;
    text = !head ? clip(body, DESCRIPTION_MAX) : room >= 40 ? `${head} – ${clip(body, room)}` : clip(head, DESCRIPTION_MAX);
  }
  if (callToAction && text && !/…$/.test(text) && text.length + callToAction.length + 2 <= DESCRIPTION_MAX) {
    text = `${text.replace(/[.!?]$/, '')}. ${callToAction}`;
  }
  return text || DEFAULT_DESCRIPTION;
};

const mediaUrl = (url, apiBase) => (/^https?:\/\//.test(url) ? url : `${apiBase}${url}`);

// First usable image of an entity, with the metadata needed for og:image:alt and the card type.
const pickImage = (entity, apiBase) => {
  const candidates = ['Bild', 'Logo', 'Produktbild'].map((field) => (((entity || {})[field] || {}).data || {}).attributes);
  candidates.push(((((entity || {}).Medien || {}).data || [])[0] || {}).attributes);
  const media = candidates.find((item) => item && item.url);
  if (!media) {
    return { url: DEFAULT_IMAGE, alt: `${SITE_NAME} Logo`, width: 0 };
  }
  return { url: mediaUrl(media.url, apiBase), alt: media.alternativeText || '', width: Number(media.width) || 0 };
};

// Large cards need a reasonably big picture; logos and tiny images look blurry cropped to 2:1.
const cardFor = (image) => (image.width >= 600 ? 'summary_large_image' : 'summary');

const ORGANIZATION_NODE = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  email: 'info@pmp-online.net',
  description: DEFAULT_DESCRIPTION,
};

// alternateName: people search for the platform as "PMP online", which also means project management.
const WEBSITE_NODE = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: SITE_URL,
  name: SITE_NAME,
  alternateName: ['PMP online', 'PMP-online.net'],
  inLanguage: 'de-DE',
  description: DEFAULT_DESCRIPTION,
  publisher: { '@id': ORGANIZATION_ID },
};

// Every indexable page gets the same graph: site + page + (entity) + breadcrumbs, linked by @id, so each
// URL declares that it belongs to PMPonline. `crumbs` is [[name, path], ...] without the home entry.
const buildGraph = ({ url, title, description, image, pageType = 'WebPage', crumbs = [], entity }) => {
  const breadcrumb = crumbs.length
    ? {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [[SITE_NAME, '/'], ...crumbs].map(([name, path], index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name,
        item: `${SITE_URL}${path}`,
      })),
    }
    : undefined;

  const page = {
    '@type': pageType,
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: 'de-DE',
    isPartOf: { '@id': WEBSITE_ID },
    breadcrumb: breadcrumb ? { '@id': breadcrumb['@id'] } : undefined,
    primaryImageOfPage: image && image !== DEFAULT_IMAGE ? { '@type': 'ImageObject', url: image } : undefined,
    mainEntity: entity ? { '@id': entity['@id'] } : undefined,
  };

  return { '@context': 'https://schema.org', '@graph': [ORGANIZATION_NODE, WEBSITE_NODE, page, entity, breadcrumb].filter(Boolean) };
};

const findEntityByPath = (pathname, veranstaltungenData) => {
  const idMatch = pathname.match(/\/(\d+)$/);
  if (!idMatch) {
    return null;
  }

  const id = Number(idMatch[1]);

  if (pathname.indexOf('/veranstaltungen/') === 0) {
    return veranstaltungenData.find((item) => item.id === id && item.type === 'Veranstaltung');
  }
  if (pathname.indexOf('/referenten/') === 0) {
    return veranstaltungenData.find((item) => item.id === id && item.type === 'Referent');
  }
  if (pathname.indexOf('/versorgungspartner/') === 0) {
    return veranstaltungenData.find((item) => item.id === id && item.type === 'Versorgungspartner');
  }
  if (pathname.indexOf('/hersteller/') === 0) {
    return veranstaltungenData.find((item) => item.id === id && item.type === 'Hersteller');
  }
  if (pathname.indexOf('/produkt/') === 0) {
    return veranstaltungenData.find((item) => item.id === id && item.type === 'Produkt');
  }

  return null;
};

// "Initiative Chronische Wunde e.V. (ICW)" -> "ICW"
const accreditationText = (entity) =>
  (entity.Fortbildungspunkte || [])
    .filter((item) => item && item.Punkte)
    .map((item) => {
      const short = String(item.Vergabestelle || '').match(/\(([^)]+)\)\s*$/);
      return `${short ? short[1] : item.Vergabestelle || ''} ${item.Punkte}`.trim();
    })
    .join(', ');

const eventWhen = (entity) => {
  if (!isValidDate(entity.Startzeit)) {
    return '';
  }
  const date = formatDate(entity.Startzeit);
  if (!isValidDate(entity.Endzeit)) {
    return `${date}, ${formatTime(entity.Startzeit)} Uhr`;
  }
  return formatDate(entity.Endzeit) === date
    ? `${date}, ${formatTime(entity.Startzeit)}–${formatTime(entity.Endzeit)} Uhr`
    : `${date} bis ${formatDate(entity.Endzeit)}`;
};

// "Frau Ines Pötzsch" -> "Ines Pötzsch"; only the publicly listed external speakers are used. Which internal
// referent speaks at an event is visible to subscribed organizers only, so it never goes into metadata.
const externalSpeakers = (entity) =>
  (entity.ExterneReferenten || [])
    .map((item) => clean((item || {}).Name).replace(/^(Frau|Herr)\s+/, ''))
    .filter((name) => name && !/\b(undefined|null)\b/i.test(name));

const buildEventSeo = (pathname, entity, image) => {
  const url = `${SITE_URL}${pathname}`;
  const name = clean(entity.Veranstaltungsname) || 'Veranstaltung';
  const online = Boolean(entity.isonlineveranstaltung);
  const city = clean(entity.Stadt);
  const organizer = clean(entity.Veranstaltername);
  const date = formatDate(entity.Startzeit);
  const ended = isValidDate(entity.Endzeit || entity.Startzeit) && new Date(entity.Endzeit || entity.Startzeit) < new Date();

  // "Wundkongress Bad Staffelstein in Bad Staffelstein" / "Basiskurs Online online" read as noise.
  const lowerName = name.toLowerCase();
  const where = online ? (lowerName.indexOf('online') === -1 ? ' online' : '') : city && lowerName.indexOf(city.toLowerCase()) === -1 ? ` in ${city}` : '';
  const when = date ? ` am ${date}` : '';
  const title = fitTitle(name, [`${where}${when}`, where, '']);

  const points = accreditationText(entity);
  const body = stripHtml(entity.Beschreibung) || listText(entity.Themengebiete, 3);
  const description = fitDescription(
    [
      [eventWhen(entity), ended ? '(beendet)' : ''].filter(Boolean).join(' '),
      online ? 'Online-Veranstaltung' : city,
      organizer && `Veranstalter: ${organizer}`,
      points && `Fortbildungspunkte: ${points}`,
    ],
    body,
    ended ? '' : 'Termin und Anmeldung auf PMPonline.'
  );

  const fee = Number(entity.Teilnehmergebuehr);
  const hasFee = entity.Teilnehmergebuehr !== null && entity.Teilnehmergebuehr !== undefined && entity.Teilnehmergebuehr !== '' && !Number.isNaN(fee);
  const [latitude, longitude] = Array.isArray(entity.Veranstaltungsort) ? entity.Veranstaltungsort.map(Number) : [];
  const geo = Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== 0 && longitude !== 0
    ? { '@type': 'GeoCoordinates', latitude, longitude }
    : undefined;
  const speakers = externalSpeakers(entity);

  const eventNode = {
    '@type': 'Event',
    '@id': `${url}#entity`,
    name,
    description,
    startDate: entity.Startzeit || undefined,
    endDate: entity.Endzeit || undefined,
    eventAttendanceMode: online ? 'https://schema.org/OnlineEventAttendanceMode' : 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    image: image.url,
    inLanguage: 'de-DE',
    location: online ? {
      '@type': 'VirtualLocation',
      url: entity.ExternerLinkOnlineVeranstaltung ? `https://${entity.ExternerLinkOnlineVeranstaltung}` : url,
    } : {
      '@type': 'Place',
      name: organizer || name,
      address: {
        '@type': 'PostalAddress',
        addressLocality: city || undefined,
        postalCode: entity.PLZ ? String(entity.PLZ) : undefined,
        addressCountry: 'DE',
      },
      geo,
    },
    organizer: organizer ? { '@type': 'Organization', name: organizer } : undefined,
    performer: speakers.length ? speakers.map((speaker) => ({ '@type': 'Person', name: speaker })) : undefined,
    offers: {
      '@type': 'Offer',
      url: entity.Anmeldelink ? `https://${String(entity.Anmeldelink).replace(/^https?:\/\//, '')}` : url,
      price: hasFee ? fee : undefined,
      priceCurrency: hasFee ? 'EUR' : undefined,
      availability: 'https://schema.org/InStock',
      validFrom: entity.publishedAt || undefined,
    },
    url,
  };

  // Google rejects an Event without a start date or a place; emit no Event rather than an invalid one.
  const complete = isValidDate(entity.Startzeit) && (online || Boolean(city));
  // An entry without name and start date is a half-filled draft (the site hides those from its lists too).
  const draft = !clean(entity.Veranstaltungsname) || !isValidDate(entity.Startzeit);

  return {
    title,
    description,
    type: 'article',
    robots: draft ? 'noindex,follow' : undefined,
    crumbs: [['Veranstaltungen', '/veranstaltungen'], [name, pathname]],
    entity: complete ? eventNode : undefined,
  };
};

const buildReferentSeo = (pathname, entity, image) => {
  const url = `${SITE_URL}${pathname}`;
  const fullName = `${entity.Titel ? `${entity.Titel} ` : ''}${entity.Vorname || ''} ${entity.Name || ''}`.replace(/\s+/g, ' ').trim();
  const city = clean(entity.Stadt);
  // Only the public Kurzbiografie is used: qualifications, fees and contact data are behind login in the UI.
  const description = fitDescription(
    [fullName, `Referent${city ? ` aus ${city}` : ''} im Wundmanagement`],
    stripHtml(entity.Bio) || stripHtml(entity.Beschreibung),
    'Profil auf PMPonline.'
  );

  const personNode = {
    '@type': 'Person',
    '@id': `${url}#entity`,
    name: fullName,
    jobTitle: 'Referent',
    description,
    image: image.url !== DEFAULT_IMAGE ? image.url : undefined,
    address: city ? {
      '@type': 'PostalAddress',
      addressLocality: city,
      postalCode: entity.PLZ ? String(entity.PLZ) : undefined,
      addressCountry: 'DE',
    } : undefined,
    url,
  };

  return {
    title: fitTitle(fullName, [` – Referent im Wundmanagement${city ? ` aus ${city}` : ''}`, ' – Referent im Wundmanagement', ' – Referent']),
    description,
    type: 'profile',
    crumbs: [['Referenten', '/referenten'], [fullName, pathname]],
    entity: personNode,
  };
};

// "Straße 1, 12345 Stadt" -> PostalAddress; anything that does not look like that is left out rather than guessed.
const parseAddress = (raw) => {
  const match = String(raw || '').match(/^(.+?),\s*(\d{5})\s+(.+)$/);
  return match ? { '@type': 'PostalAddress', streetAddress: match[1].trim(), postalCode: match[2], addressLocality: match[3].trim(), addressCountry: 'DE' } : undefined;
};

const buildHerstellerSeo = (pathname, entity, image, allData) => {
  const url = `${SITE_URL}${pathname}`;
  const name = clean(entity.Herstellername) || 'Hersteller';
  const count = allData.filter((item) => item.type === 'Produkt' && item.HerstellerId === entity.id).length;
  const description = fitDescription(
    [name, count ? `${count} ${count === 1 ? 'Produkt' : 'Produkte'} für Wundversorgung und Wundmanagement` : 'Hersteller im Wundmanagement'],
    stripHtml(entity.Beschreibung),
    'Produkte und Infos auf PMPonline.'
  );

  const organizationNode = {
    '@type': 'Organization',
    '@id': `${url}#entity`,
    name,
    description,
    logo: image.url !== DEFAULT_IMAGE ? image.url : undefined,
    url: entity.Homepage ? `https://${entity.Homepage}` : url,
    address: parseAddress(entity.Addresse),
  };

  return {
    title: fitTitle(name, [' – Produkte & Hersteller-Infos', ' – Produkte', '']),
    description,
    type: 'article',
    crumbs: [['Hersteller', '/hersteller'], [name, pathname]],
    entity: organizationNode,
  };
};

const buildProduktSeo = (pathname, entity, image) => {
  const url = `${SITE_URL}${pathname}`;
  const name = clean(entity.Produktname) || 'Produkt';
  const pzn = clean(entity.PZN);
  const maker = clean(entity.Hersteller);
  const category = [clean(entity.Hauptkategorie), clean(entity.Unterkategorie)].filter(Boolean).join(' › ');
  // Pharmacies, nurses and buyers look products up by PZN, so it goes into the title and the snippet.
  const title = fitTitle(name, [
    `${maker ? ` – ${maker}` : ''}${pzn ? ` (PZN ${pzn})` : ''}`,
    pzn ? ` (PZN ${pzn})` : '',
    maker ? ` – ${maker}` : '',
    '',
  ]);

  // Variants of one product share a long text; the name, pack and PZN up front keep every snippet unique.
  const indications = stripHtml(entity.Indikationen).replace(/^Indikationen\s*/i, '');
  const description = fitDescription(
    [`${name}${maker ? ` von ${maker}` : ''}`, pzn && `PZN ${pzn}`, category],
    stripHtml(entity.Produktbeschreibung) || indications,
    'Produktinfos auf PMPonline.'
  );

  const productNode = {
    '@type': 'Product',
    '@id': `${url}#entity`,
    name,
    description,
    sku: pzn || undefined,
    mpn: entity.HerstellerArtikelnummer ? String(entity.HerstellerArtikelnummer).trim() : undefined,
    productID: pzn ? `PZN:${pzn}` : undefined,
    brand: maker ? { '@type': 'Brand', name: maker } : undefined,
    image: image.url !== DEFAULT_IMAGE ? image.url : undefined,
    category: category || undefined,
    url,
  };

  const crumbs = [['Hersteller', '/hersteller']];
  if (maker && entity.HerstellerId) {
    crumbs.push([maker, `/hersteller/${entity.HerstellerId}`]);
  }
  crumbs.push([name, pathname]);

  return { title, description, type: 'product', crumbs, entity: productNode };
};

const buildPartnerSeo = (pathname, entity, image) => {
  const url = `${SITE_URL}${pathname}`;
  const name = clean(entity.Name) || 'Versorgungspartner';
  const city = clean(entity.Stadt);
  const kind = clean(entity.Kategorie);
  const place = [entity.PLZ, city].filter(Boolean).join(' ');
  const tasks = listText(entity.Aufgabengebiete);
  const treatments = listText(entity.Spezialbehandlungen);
  const description = fitDescription(
    [name, kind, place && `in ${place}`, tasks && `Schwerpunkte: ${tasks}`, treatments && `Spezialbehandlungen: ${treatments}`],
    stripHtml(entity.description),
    'Versorgungspartner auf PMPonline.'
  );

  const knowsAbout = [...flatten(entity.Aufgabengebiete), ...flatten(entity.Spezialbehandlungen)].filter((item) => item && typeof item === 'string');
  const partnerNode = {
    '@type': 'Organization',
    '@id': `${url}#entity`,
    name,
    description,
    url: entity.Homepage ? `https://${entity.Homepage}` : url,
    logo: image.url !== DEFAULT_IMAGE ? image.url : undefined,
    knowsAbout: knowsAbout.length ? knowsAbout : undefined,
    address: (entity.Stadt || entity.PLZ) ? {
      '@type': 'PostalAddress',
      addressLocality: entity.Stadt || undefined,
      postalCode: entity.PLZ ? String(entity.PLZ) : undefined,
      addressRegion: entity.Bundesland || undefined,
      addressCountry: 'DE',
    } : undefined,
  };

  return {
    title: fitTitle(name, [`${kind ? ` – ${kind}` : ''}${city ? ` in ${city}` : ''}`, city ? ` in ${city}` : '', '']),
    description,
    type: 'article',
    crumbs: [['Versorgungspartner', '/versorgungspartner'], [name, pathname]],
    entity: partnerNode,
  };
};

const buildEntitySeo = (pathname, entity, apiBase, allData) => {
  if (!entity) {
    return {
      title: `Inhalt nicht gefunden | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      image: DEFAULT_IMAGE,
      imageAlt: `${SITE_NAME} Logo`,
      twitterCard: 'summary',
      type: 'website',
      robots: 'noindex,nofollow',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Inhalt nicht gefunden',
        url: `${SITE_URL}${pathname}`,
      },
    };
  }

  const url = `${SITE_URL}${pathname}`;
  const image = pickImage(entity, apiBase);

  let built = null;
  if (pathname.indexOf('/veranstaltungen/') === 0) {
    built = buildEventSeo(pathname, entity, image);
  } else if (pathname.indexOf('/referenten/') === 0) {
    built = buildReferentSeo(pathname, entity, image);
  } else if (pathname.indexOf('/versorgungspartner/') === 0) {
    built = buildPartnerSeo(pathname, entity, image);
  } else if (pathname.indexOf('/hersteller/') === 0) {
    built = buildHerstellerSeo(pathname, entity, image, allData);
  } else if (pathname.indexOf('/produkt/') === 0) {
    built = buildProduktSeo(pathname, entity, image);
  }
  if (!built) {
    return null;
  }

  const { crumbs, entity: entityNode, ...seo } = built;
  return {
    ...seo,
    image: image.url,
    imageAlt: image.alt || seo.title.replace(/ \| PMPonline$/, ''),
    twitterCard: cardFor(image),
    robots: seo.robots || ROBOTS_INDEX,
    schema: buildGraph({ url, title: seo.title, description: seo.description, image: image.url, crumbs, entity: entityNode }),
  };
};

const CRUMB_NAMES = {
  '/veranstaltungen': 'Veranstaltungen',
  '/referenten': 'Referenten',
  '/hersteller': 'Hersteller',
  '/versorgungspartner': 'Versorgungspartner',
  '/info': 'Über PMPonline',
  '/abo': 'Abonnements',
  '/app': 'PMPonline App',
  '/datenschutz': 'Datenschutz',
  '/impressum': 'Impressum',
};

const PAGE_TYPES = {
  '/veranstaltungen': 'CollectionPage',
  '/referenten': 'CollectionPage',
  '/hersteller': 'CollectionPage',
  '/versorgungspartner': 'CollectionPage',
  '/info': 'AboutPage',
};

const buildSeoState = (pathname, veranstaltungenData, apiBase) => {
  const normalizedPath = pathname === '' ? '/' : pathname;

  if (ADMIN_PREFIXES.some((prefix) => normalizedPath.indexOf(prefix) === 0)) {
    return {
      title: `Interner Bereich | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      image: DEFAULT_IMAGE,
      imageAlt: `${SITE_NAME} Logo`,
      twitterCard: 'summary',
      type: 'website',
      robots: 'noindex,nofollow',
      canonical: `${SITE_URL}${normalizedPath}`,
      schema: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Interner Bereich',
        url: `${SITE_URL}${normalizedPath}`,
      },
    };
  }

  const staticRoute = STATIC_ROUTES[normalizedPath];
  if (staticRoute) {
    const url = `${SITE_URL}${normalizedPath}`;
    const indexable = staticRoute.robots.indexOf('noindex') === -1;
    return {
      ...staticRoute,
      image: DEFAULT_IMAGE,
      imageAlt: `${SITE_NAME} Logo`,
      twitterCard: 'summary',
      canonical: url,
      schema: indexable ? buildGraph({
        url,
        title: staticRoute.title,
        description: staticRoute.description,
        pageType: PAGE_TYPES[normalizedPath] || 'WebPage',
        crumbs: normalizedPath === '/' ? [] : [[CRUMB_NAMES[normalizedPath] || staticRoute.title.split(' | ')[0], normalizedPath]],
      }) : {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: staticRoute.title,
        description: staticRoute.description,
        url,
      },
    };
  }

  const entitySeo = buildEntitySeo(normalizedPath, findEntityByPath(normalizedPath, veranstaltungenData), apiBase, veranstaltungenData);
  if (entitySeo) {
    return {
      ...entitySeo,
      canonical: `${SITE_URL}${normalizedPath}`,
    };
  }

  return {
    title: `${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_IMAGE,
    imageAlt: `${SITE_NAME} Logo`,
    twitterCard: 'summary',
    type: 'website',
    robots: 'noindex,nofollow',
    canonical: `${SITE_URL}${normalizedPath}`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: SITE_NAME,
      description: DEFAULT_DESCRIPTION,
      url: `${SITE_URL}${normalizedPath}`,
    },
  };
};

// Detail routes whose metadata can only be derived once the entity data has been fetched.
const ENTITY_PATH = /^\/(veranstaltungen|referenten|versorgungspartner|hersteller|produkt)\/\d+$/;
const isEntityPath = (pathname) => ENTITY_PATH.test(pathname);

export {
  SITE_URL,
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  STATIC_ROUTES,
  ROBOTS_INDEX,
  buildSeoState,
  isEntityPath,
  formatDate,
  stripHtml,
};
